import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { login } from "../../redux/authSlice";
import api from "../../utils/axios";
import { toast } from "react-toastify";
import "./Login.css";
import DefaultInput from "../../components/form/DefaultInput";
import { Button, Modal } from "antd";
import { LoginResponseDocumentation } from "../../types/enums";
import useLang from "../../hooks/useLang";
import { ReactComponent as SquareLogin } from "../../assets/loginSquare.svg";
import isAuthenticated from "../../utils/isAuthenticated";
import DefaultPasswordInput from "../../components/form/DefaultPasswordInput";
import { UAParser } from "ua-parser-js";

const Login = () => {
	const navigate = useNavigate();
	const { strings } = useLang("login");
	const dispatch = useDispatch();
	const [username, setUsername] = useState("");
	const [password, setPassword] = useState("");

	const [mfaCode, setMfaCode] = useState<string>("");
	const [isMfaStep, setIsMfaStep] = useState<boolean>(false);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [pendingLoginResponse, setPendingLoginResponse] = useState<any>(null);
	const [mfaPhoneNumber, setMfaPhoneNumber] = useState<string>("");

	useEffect(() => {
		if (isAuthenticated()) {
			navigate("/");
		}
	}, []);

	const completeLogin = (loginResponse: any) => {
		const authToken = loginResponse.authToken;
		const docReqType = loginResponse.requiresDocumentation;
		dispatch(login({ token: authToken }));
		localStorage.setItem("token", authToken);
		localStorage.setItem(
			"requiresDocumentation",
			String(loginResponse.requiresDocumentation)
		);
		if (docReqType) {
			navigate("/user-document-restore");
		} else {
			navigate("/dashboard");
		}
	};

	const openMfaStep = async (responseData: any) => {
		const phoneNumber = responseData?.lastPhoneDigits || "";
		setPendingLoginResponse(responseData);
		setMfaPhoneNumber(phoneNumber);
		setIsMfaStep(true);
		setMfaCode("");

		if (phoneNumber) {
			try {
				await api.post("OTP/insert-otp", {
					numbers: [phoneNumber],
				});
			} catch (error) {
				console.log(error);
			}
		}
	};

	const handleSubmit = async (e: any) => {
		e.preventDefault();
		if (
			!username?.toString()?.trim()?.length ||
			!password?.toString()?.trim()?.length
		) {
			return;
		}
		const parser = new UAParser();
		const result = parser.getResult();

		const device =
			result.browser.name +
			"(" +
			result.os.name +
			")" +
			(result.device.model ? `-${result.device.model}` : "");
		setIsSubmitting(true);
		try {
			const response = await api.post("User/web-login", {
				username,
				password,
				deviceModel: device,
			});
			const requireMfa = response?.data?.isMfaEnabled;
			if (requireMfa) {
				await openMfaStep(response.data);
				return;
			}
			completeLogin(response.data);
			return response.data.accessToken;
		} catch (error: any) {
			toast.error("Error");
			console.log(error?.response?.data);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleKeyDown = (e: any) => {
		if (e.key === "Enter") {
			handleSubmit(e);
		}
		// if (isMfaStep) {
		// 	verifyMfaCode();
		// 	return;
		// }
	};

	const getMaskedPhone = (phone: string) => {
		const digits = phone?.replace(/\D+/g, "") || "";
		if (!digits.length) {
			return "••••";
		}
		const visibleDigits = Math.min(
			3,
			Math.max(2, digits.length >= 3 ? 3 : 2)
		);
		const tail = digits.slice(-visibleDigits);
		return `********${tail}`;
	};

	const verifyMfaCode = async () => {
		if (!mfaCode.trim()) {
			toast.error(
				strings?.ui?.mfaCodeRequired || "Please enter the code"
			);
			return;
		}
		const parser = new UAParser();
		const result = parser.getResult();

		const device =
			result.browser.name +
			"(" +
			result.os.name +
			")" +
			(result.device.model ? `-${result.device.model}` : "");

		const bodyRequest = {
			code: mfaCode,
			username,
			password,
			deviceModel: device,
		};
		setIsSubmitting(true);
		try {
			const response = await api.post("User/otp-login", bodyRequest);
			if (response.status === 200) {
				console.log(response.data, "resposdfsdjnf");
				completeLogin(response.data);
				return;
			}
			toast.error(strings?.ui?.mfaCodeInvalid || "Invalid code.");
		} catch (error: any) {
			toast.error(strings?.ui?.mfaCodeInvalid || "Invalid code.");
			console.log(error?.response?.data);
		} finally {
			setIsSubmitting(false);
		}
	};

	const resetMfaState = () => {
		setIsMfaStep(false);
		setMfaCode("");
		setPendingLoginResponse(null);
		setMfaPhoneNumber("");
	};

	return (
		<div className="loginMainContainer">
			<SquareLogin className="square" />
			<div className="loginContainer">
				<div className="loginInfos">
					<h1 className="loginTitle">{strings?.ui.loginTitle}</h1>
					{!isMfaStep ? (
						<div onKeyDown={handleKeyDown}>
							<DefaultInput
								label={strings?.ui.usernameLabel}
								className="loginInput"
								placeholder={strings?.ui.usernamePlaceholder}
								value={username}
								onChange={(e) => setUsername(e.target.value)}
							/>
							<DefaultPasswordInput
								className="loginInput"
								label={strings?.ui.passwordLabel}
								value={password}
								placeholder={strings?.ui.passwordPlaceholder}
								onChange={(e) => setPassword(e.target.value)}
								allowClear={true}
							/>
						</div>
					) : (
						<>
							<p className="mfaDescription">
								{strings?.ui?.mfaDescription ||
									"Enter the code we sent to your phone ending in"}{" "}
								<strong>
									{getMaskedPhone(mfaPhoneNumber)}
								</strong>
							</p>
							<DefaultInput
								label={
									strings?.ui?.mfaCodeLabel || "One-time code"
								}
								className="loginInput"
								placeholder={
									strings?.ui?.mfaCodePlaceholder ||
									"Enter verification code"
								}
								value={mfaCode}
								onChange={(e) =>
									setMfaCode(
										e.target.value.replace(/\D+/g, "")
									)
								}
							/>
						</>
					)}
				</div>
				<div className="buttonsLogin">
					{!isMfaStep ? (
						<>
							<Button
								className="pwButton"
								onClick={() => navigate("/password-recovery")}
							>
								{strings?.ui.pwButton}
							</Button>
							<Button
								className="loginButton"
								onClick={handleSubmit}
							>
								{strings?.ui.loginButton}
							</Button>
						</>
					) : (
						<>
							<>
								<Button
									className="pwButton"
									onClick={resetMfaState}
								>
									{strings?.ui?.mfaBackButton || "Back"}
								</Button>
								<Button
									className="loginButton"
									onClick={verifyMfaCode}
									loading={isSubmitting}
								>
									{strings?.ui?.mfaVerifyButton ||
										"Verify code"}
								</Button>
							</>
						</>
					)}
				</div>
				{!isMfaStep && (
					<div className="registerLogin">
						<h2>
							{strings?.ui.noAccount}{" "}
							<Link to={"/register"} className="registerText">
								{strings?.ui.registerText}
							</Link>
						</h2>
					</div>
				)}
			</div>
			<SquareLogin className="square" />
		</div>
	);
};

export default Login;
